import { relocationDetailHtml, relocationDetailLoadingHtml, relocationReturnConfirmationHtml, relocationTransitConfirmationHtml,
  type RelocationDetail, type RelocationReturnItem, type RelocationReturnPlan } from './inventory-relocation-detail';
type Api = <T>(path: string, options?: RequestInit) => Promise<T>;
type ReturnResult = { results: Array<{ groupName: string; ok: boolean; updated?: number; message?: string }> };
function busy(dialog: HTMLDialogElement, value: boolean) {
  dialog.toggleAttribute('data-shopify-busy', value);
  dialog.setAttribute('aria-busy', String(value));
  dialog.querySelectorAll<HTMLButtonElement>('button').forEach((b) => { b.disabled = value; });
}
function confirmRelocationTransit(parent: HTMLDialogElement, show: (dialog: HTMLDialogElement) => void): Promise<boolean> {
  const confirmation = parent.ownerDocument.createElement('dialog');
  confirmation.className = 'inventory-transfer-request-dialog inventory-shopify-message-dialog';
  confirmation.setAttribute('aria-labelledby', 'inventory-transit-confirm-title');
  confirmation.setAttribute('aria-describedby', 'inventory-transit-confirm-warning');
  confirmation.innerHTML = relocationTransitConfirmationHtml();
  parent.ownerDocument.body.appendChild(confirmation);
  return new Promise((resolve, reject) => {
    let done = false;
    const finish = (confirmed: boolean) => {
      if (done) return;
      done = true;
      if (confirmation.open) confirmation.close();
      confirmation.remove();
      resolve(confirmed && parent.open && parent.isConnected);
    };
    confirmation.addEventListener('close', () => finish(false), { once: true });
    confirmation.addEventListener('cancel', (event) => { event.preventDefault(); finish(false); });
    confirmation.querySelector('[data-transit-cancel]')?.addEventListener('click', () => finish(false));
    confirmation.querySelector('[data-transit-confirm]')?.addEventListener('click', () => finish(true));
    try { show(confirmation); } catch (error) { confirmation.remove(); reject(error); }
  });
}

export async function openRelocationDetail(dialog: HTMLDialogElement, groupName: string, api: Api, show: (dialog: HTMLDialogElement) => void, refresh: () => void) {
  if (dialog.open) return;
  dialog.innerHTML = relocationDetailLoadingHtml();
  let submitting = true;
  let attempted = false;
  dialog.oncancel = (e) => { if (submitting) e.preventDefault(); };
  show(dialog);
  try {
    const detail = await api<RelocationDetail>(`/api/inventory-relocation-details?groupName=${encodeURIComponent(groupName)}`);
    dialog.innerHTML = relocationDetailHtml(detail);
    submitting = false;
    dialog.querySelector('[data-detail-close]')?.addEventListener('click', () => { if (!submitting) dialog.close(); });
    dialog.onclose = () => {
      // A queued close event from a previous opening must not consume the new
      // dialog's completion handler when the user reopens it immediately.
      if (dialog.open) return;
      dialog.onclose = null;
      if (attempted) refresh();
    };
    dialog.querySelector('[data-detail-transit]')?.addEventListener('click', async () => {
      if (submitting || attempted || !detail.canTransit) return;
      submitting = true;
      try {
        if (!await confirmRelocationTransit(dialog, show)) return;
      } catch (error) {
        dialog.querySelector<HTMLElement>('[data-action-result]')!.textContent = error instanceof Error ? error.message : '確認画面を開けませんでした。';
        return;
      } finally { submitting = false; }
      submitting = true; attempted = true; busy(dialog, true);
      const result = dialog.querySelector<HTMLElement>('[data-action-result]')!;
      result.textContent = 'Shopifyの移管を移動中に変更しています…';
      try {
        await api('/api/inventory-relocation-details', { method: 'POST', body: JSON.stringify({ groupName, version: detail.version }) });
        result.textContent = 'Shopifyの在庫移管を移動中に変更しました。';
        const badge = dialog.querySelector<HTMLElement>('.inventory-replenishment-status');
        if (badge) badge.textContent = '移動中';
      } catch (error) {
        result.textContent = `${error instanceof Error ? error.message : '変更結果を確認できませんでした。'} 閉じて同じグループの詳細から再確認してください。`;
      } finally {
        submitting = false; busy(dialog, false);
        const submit = dialog.querySelector<HTMLButtonElement>('[data-detail-transit]')!;
        submit.disabled = true; submit.textContent = '処理終了';
      }
    });
  } catch (error) {
    dialog.close();
    throw error;
  } finally { submitting = false; }
}
export async function confirmRelocationAdminReturn(dialog: HTMLDialogElement, api: Api, items: RelocationReturnItem[], passcode: string): Promise<ReturnResult | null> {
  dialog.innerHTML = relocationDetailLoadingHtml().replace('Shopify 在庫移管詳細', '元に戻す内容を確認中');
  dialog.oncancel = (event) => event.preventDefault();
  const { plans } = await api<{ plans: RelocationReturnPlan[] }>('/api/inventory-relocation-admin-returns',
    { method: 'POST', body: JSON.stringify({ items, passcode, confirm: false }) });
  dialog.innerHTML = relocationReturnConfirmationHtml(plans);
  return new Promise((resolve, reject) => {
    let submitting = false; let done = false;
    const finish = (result: ReturnResult | null) => { if (!done) { done = true; cleanup(); resolve(result); } };
    const onClose = () => { if (!submitting) finish(null); };
    const cleanup = () => { dialog.removeEventListener('close', onClose); dialog.oncancel = null; passcode = ''; };
    dialog.addEventListener('close', onClose);
    dialog.oncancel = (event) => { event.preventDefault(); if (!submitting) finish(null); };
    dialog.querySelector('[data-admin-return-cancel]')?.addEventListener('click', () => { if (!submitting) finish(null); });
    dialog.querySelector('[data-admin-return-confirm]')?.addEventListener('click', async () => {
      if (submitting || done) return;
      submitting = true; busy(dialog, true);
      const label = dialog.querySelector<HTMLElement>('[data-action-result]');
      if (label) label.textContent = 'Shopifyの下書きを削除し、選択商品を戻しています…';
      try {
        const result = await api<ReturnResult>('/api/inventory-relocation-admin-returns',
          { method: 'POST', body: JSON.stringify({ items, passcode, confirm: true, plans: plans.map((p) => ({ groupName: p.groupName, version: p.version })) }) });
        finish(result);
      } catch (error) { done = true; cleanup(); reject(error); }
      finally { submitting = false; busy(dialog, false); }
    });
  });
}
